// src/components/ApplicationWebToLead.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/ApplicationForm.css";

const ApplicationWebToLead = () => {
  useEffect(() => {
    // Load Google reCAPTCHA script (only once)
    const existing = document.querySelector(
      'script[src="https://www.google.com/recaptcha/api.js"]'
    );
    if (!existing) {
      const s = document.createElement("script");
      s.src = "https://www.google.com/recaptcha/api.js";
      s.async = true;
      s.defer = true;
      document.body.appendChild(s);
    }

    // Mirror Salesforce-generated timestamp updater for captcha_settings
    const interval = setInterval(() => {
      const response = document.getElementById("g-recaptcha-response");
      const captchaSettingsInput = document.getElementsByName("captcha_settings")?.[0];
      if (!captchaSettingsInput) return;

      try {
        if (!response || response.value.trim() === "") {
          const elems = JSON.parse(captchaSettingsInput.value);
          elems.ts = JSON.stringify(new Date().getTime());
          captchaSettingsInput.value = JSON.stringify(elems);
        }
      } catch (e) {
        // no-op
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // ---------------------------
  // Helpers: money formatting
  // ---------------------------
  const parseCurrencyToNumber = (val) => {
    const cleaned = String(val ?? "").replace(/[^0-9.-]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const formatCurrencyNoCents = (num) => {
    if (num === null || num === undefined || !Number.isFinite(num)) return "";
    const rounded = Math.round(num);
    return rounded.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  };

  /**
   * For inputs:
   * - keep a "raw" string while typing (so user can type naturally)
   * - onBlur, format into $X,XXX
   */
  const onCurrencyBlur = (value, setter) => {
    const n = parseCurrencyToNumber(value);
    if (n === null) return; // leave as-is if blank or invalid
    setter(formatCurrencyNoCents(n));
  };

  // ---------------------------
  // Helpers: EIN formatting XX-XXXXXXX
  // ---------------------------
  const formatEIN = (val) => {
    const digits = String(val ?? "").replace(/\D/g, "").slice(0, 9);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  const einDigitsOnly = (val) => String(val ?? "").replace(/\D/g, "").slice(0, 9);

  // ---------------------------
  // Helpers: Social handles -> full URLs
  // ---------------------------
  const cleanIdentifier = (input) => {
    let s = String(input ?? "").trim();
    if (!s) return "";
    s = s.replace(/^https?:\/\//i, "");
    s = s.replace(/^www\./i, "");
    s = s.replace(/^\//, "");
    s = s.replace(/\s+/g, "");
    return s;
  };

  const buildLinkedInUrl = (input) => {
    const s = cleanIdentifier(input).toLowerCase();
    if (!s) return "";
    // accept "in/leighton", "/in/leighton", "linkedin.com/in/leighton"
    let handle = s;
    handle = handle.replace(/^linkedin\.com\//i, "");
    handle = handle.replace(/^in\//i, "");
    handle = handle.replace(/^profile\//i, "");
    handle = handle.replace(/^pub\//i, "");
    handle = handle.replace(/^company\//i, ""); // just in case
    handle = handle.replace(/^\/+/, "");
    return `https://linkedin.com/in/${handle}`;
  };

  const buildInstagramUrl = (input) => {
    const s = cleanIdentifier(input).toLowerCase();
    if (!s) return "";
    // accept "@name", "instagram.com/name", "/name"
    let handle = s.replace(/^instagram\.com\//i, "");
    handle = handle.replace(/^@/, "");
    handle = handle.replace(/^\/+/, "");
    return `https://instagram.com/${handle}`;
  };

  const buildFacebookUrl = (input) => {
    const s = cleanIdentifier(input).toLowerCase();
    if (!s) return "";
    // accept "facebook.com/name", "/name"
    let handle = s.replace(/^facebook\.com\//i, "");
    handle = handle.replace(/^\/+/, "");
    return `https://facebook.com/${handle}`;
  };

  // =========================
  // Fundraising (Operational SF Multi-Picklist)
  // =========================
  const FUNDRAISING_OPTIONS = useMemo(
    () => [
      "Individual Donations",
      "Corporate Donations",
      "Personal Contributions",
      "Private Grants",
      "Government Grants",
      "Online Fundraising",
      "In-Person Fundraising Events",
    ],
    []
  );

  const [fundraisingSelected, setFundraisingSelected] = useState([]);
  const fundraisingSelectRef = useRef(null);

  const hasGovernmentGrants = useMemo(
    () => fundraisingSelected.includes("Government Grants"),
    [fundraisingSelected]
  );

  const toggleFundraising = (option) => {
    setFundraisingSelected((prev) => {
      if (prev.includes(option)) return prev.filter((x) => x !== option);
      return [...prev, option];
    });
  };

  useEffect(() => {
    if (!fundraisingSelectRef.current) return;
    const select = fundraisingSelectRef.current;
    const selectedSet = new Set(fundraisingSelected);
    Array.from(select.options).forEach((opt) => {
      opt.selected = selectedSet.has(opt.value);
    });
  }, [fundraisingSelected]);

  // =========================
  // Organization Details (Operational: picklist + EIN; Non-op: advisor address)
  // =========================
  const [entityType, setEntityType] = useState(""); // Individual | Entity
  const [ein, setEin] = useState("");
  const [advisorHomeAddress, setAdvisorHomeAddress] = useState("");

  useEffect(() => {
    if (entityType === "Entity") {
      setAdvisorHomeAddress("");
    } else if (entityType === "Individual") {
      setEin("");
    } else {
      setEin("");
      setAdvisorHomeAddress("");
    }
  }, [entityType]);

  // =========================
  // Non-operational questions (concatenate into 2 SF Long Text fields)
  // =========================
  const [firstYearRevenue, setFirstYearRevenue] = useState("");
  const [secondYearRevenue, setSecondYearRevenue] = useState("");

  const [budget, setBudget] = useState({
    programmaticSupplies: "",
    contractorPayments: "",
    vendorPayments: "",
    scholarships: "",
    travel: "",
    marketing: "",
    administrativeCosts: "",
    other: "",
  });

  const budgetTotalNumber = useMemo(() => {
    const toNum = (v) => {
      const n = parseCurrencyToNumber(v);
      return n === null ? 0 : n;
    };
    return Object.values(budget).reduce((sum, v) => sum + toNum(v), 0);
  }, [budget]);

  const budgetTotalFormatted = useMemo(
    () => formatCurrencyNoCents(budgetTotalNumber),
    [budgetTotalNumber]
  );

  const [govFundingExploring, setGovFundingExploring] = useState(""); // Yes/No (only if Govt Grants selected)
  useEffect(() => {
    if (!hasGovernmentGrants) setGovFundingExploring("");
  }, [hasGovernmentGrants]);

  const [fiscallySponsoredBefore, setFiscallySponsoredBefore] = useState(""); // Yes/No
  const [transferFunds, setTransferFunds] = useState(""); // Yes/No
  const [transferAmount, setTransferAmount] = useState("");

  useEffect(() => {
    if (transferFunds !== "Yes") setTransferAmount("");
  }, [transferFunds]);

  const [additionalComments, setAdditionalComments] = useState("");

  // =========================
  // Social inputs (user types identifier; SF receives full URL)
  // =========================
  const linkedInSfRef = useRef(null); // textarea id 00N7z00000m00Nl
  const instagramSfRef = useRef(null); // textarea id 00N7z00000m00E6
  const facebookSfRef = useRef(null); // textarea id 00N7z00000m00PN

  const [linkedInId, setLinkedInId] = useState("");
  const [instagramId, setInstagramId] = useState("");
  const [facebookId, setFacebookId] = useState("");

  // =========================
  // Hidden SF Long Text fields (already provided by your HTML)
  // =========================
  const accountNotesRef = useRef(null); // 00N7z00000nHLv3
  const opportunityNotesRef = useRef(null); // 00N7z00000nHLyH

  const buildAccountNotes = () => {
    const lines = [];

    if (entityType) lines.push(`Applying As: ${entityType}`);
    if (entityType === "Individual" && advisorHomeAddress.trim()) {
      lines.push(`Advisor Home Address: ${advisorHomeAddress.trim()}`);
    }

    if (additionalComments.trim()) lines.push(`Additional Comments: ${additionalComments.trim()}`);

    return lines.join("\n");
  };

  const buildOpportunityNotes = () => {
    const lines = [];

    // Force money format in notes ($X,XXX) rounded to the dollar
    const fyNum = parseCurrencyToNumber(firstYearRevenue);
    const syNum = parseCurrencyToNumber(secondYearRevenue);

    if (fyNum !== null) lines.push(`First Year Revenue Projection: ${formatCurrencyNoCents(fyNum)}`);
    if (syNum !== null) lines.push(`Second Year Revenue Projection: ${formatCurrencyNoCents(syNum)}`);

    const b = budget;
    const budgetLines = [];
    const addBudgetLine = (label, val) => {
      const n = parseCurrencyToNumber(val);
      if (n !== null) budgetLines.push(`${label}: ${formatCurrencyNoCents(n)}`);
    };

    addBudgetLine("Programmatic Supplies", b.programmaticSupplies);
    addBudgetLine("Contractor Payments", b.contractorPayments);
    addBudgetLine("Vendor Payment", b.vendorPayments);
    addBudgetLine("Scholarships", b.scholarships);
    addBudgetLine("Travel", b.travel);
    addBudgetLine("Marketing", b.marketing);
    addBudgetLine("Administrative Costs", b.administrativeCosts);
    addBudgetLine("Other", b.other);

    if (budgetLines.length) {
      lines.push("Budget Template:");
      lines.push(...budgetLines.map((x) => `  - ${x}`));
      lines.push(`Budget Total: ${budgetTotalFormatted || "$0"}`);
    }

    if (hasGovernmentGrants) {
      lines.push(
        `Pursuing/Exploring Government Funding Opportunities?: ${
          govFundingExploring || "Not answered"
        }`
      );
    }

    if (fiscallySponsoredBefore) {
      lines.push(`Previously Fiscally Sponsored?: ${fiscallySponsoredBefore}`);
    }

    if (transferFunds) lines.push(`Existing Funds to Transfer?: ${transferFunds}`);
    if (transferFunds === "Yes") {
      const tNum = parseCurrencyToNumber(transferAmount);
      if (tNum !== null) lines.push(`Amount to be transferred (USD): ${formatCurrencyNoCents(tNum)}`);
      else if (transferAmount.trim()) lines.push(`Amount to be transferred (USD): ${transferAmount.trim()}`);
    }

    return lines.join("\n");
  };

  // =========================
  // Submit: set hidden fields + set social URLs + enforce Lead Source hidden
  // =========================
  const handleSubmit = (e) => {
    // Conditional requirements per your meeting
    if (entityType === "Entity" && einDigitsOnly(ein).length !== 9) {
      e.preventDefault();
      alert("Please provide a valid EIN (9 digits).");
      return;
    }
    if (entityType === "Individual" && !advisorHomeAddress.trim()) {
      e.preventDefault();
      alert("Please provide Advisor Home Address (required when applying as an Individual).");
      return;
    }
    if (hasGovernmentGrants && !govFundingExploring) {
      e.preventDefault();
      alert("Please answer the government funding question.");
      return;
    }
    if (transferFunds === "Yes") {
      const tNum = parseCurrencyToNumber(transferAmount);
      if (tNum === null) {
        e.preventDefault();
        alert("Please enter the amount to be transferred (USD).");
        return;
      }
    }

    // Populate concatenated note fields (SF Long Text Areas)
    if (accountNotesRef.current) accountNotesRef.current.value = buildAccountNotes();
    if (opportunityNotesRef.current) opportunityNotesRef.current.value = buildOpportunityNotes();

    // Social media: applicant types identifier; SF gets full URL
    if (linkedInSfRef.current) linkedInSfRef.current.value = buildLinkedInUrl(linkedInId);
    if (instagramSfRef.current) instagramSfRef.current.value = buildInstagramUrl(instagramId);
    if (facebookSfRef.current) facebookSfRef.current.value = buildFacebookUrl(facebookId);
  };

  return (
    <div className="application-wrapper">
      <form
        className="application-form"
        action="https://test.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00D7z00000PZ0ef"
        method="POST"
        onSubmit={handleSubmit}
      >
        <input
          type="hidden"
          name="captcha_settings"
          value='{"keyname":"CauseImpact_WebToLead","fallback":"true","orgId":"00D7z00000PZ0ef","ts":""}'
        />
        <input type="hidden" name="oid" value="00D7z00000PZ0ef" />
        <input type="hidden" name="retURL" value="https://causeandimpact.org/application-submitted" />

        {/* Lead Source should NOT be selectable by applicant */}
        <input type="hidden" id="lead_source" name="lead_source" value="Web" />

        <h2>Fiscal Sponsorship Application</h2>
        <p>Answer a few questions so we can review your Cause and follow up.</p>

        {/* ======================================================
            1) Personal Information (Operational)
           ====================================================== */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Personal Information</h3>
            <p>Tell us who is submitting this application.</p>
          </div>

          <div className="form-row form-row-2">
            <label htmlFor="first_name">
              First Name
              <input id="first_name" maxLength="40" name="first_name" type="text" required />
            </label>

            <label htmlFor="last_name">
              Last Name
              <input id="last_name" maxLength="80" name="last_name" type="text" required />
            </label>
          </div>

          <div className="form-row form-row-2">
            <label htmlFor="email">
              Email
              <input id="email" maxLength="80" name="email" type="text" required />
            </label>

            <label htmlFor="mobile">
              Mobile Phone
              <input id="mobile" maxLength="40" name="mobile" type="text" required />
            </label>
          </div>

          <div className="form-row">
            <label htmlFor="phone">
              Office Phone (Optional)
              <input id="phone" maxLength="40" name="phone" type="text" />
            </label>
          </div>
        </div>

        <div className="form-section-separator" />

        {/* ======================================================
            2) Cause Details (Operational)
           ====================================================== */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Cause Details</h3>
            <p>Tell us about your Cause and its mission.</p>
          </div>

          <div className="form-row form-row-2">
            <label htmlFor="company">
              Cause Name
              <input id="company" maxLength="40" name="company" type="text" required />
            </label>

            <label htmlFor="url">
              Cause URL
              <input id="url" maxLength="80" name="url" type="text" placeholder="e.g., www.example.com" />
            </label>
          </div>

          <div className="form-row location-inputs">
            <label htmlFor="city">
              Location (City)
              <input id="city" maxLength="40" name="city" type="text" />
            </label>

            <label htmlFor="state">
              Location (State)
              <input id="state" maxLength="20" name="state" type="text" />
            </label>
          </div>

          <div className="form-row">
            <label htmlFor="00N7z00000lzzl3">
              Mission Statement
              <textarea
                id="00N7z00000lzzl3"
                name="00N7z00000lzzl3"
                wrap="soft"
                rows={4}
                placeholder="Describe your mission in 255 characters or less"
              />
            </label>
          </div>
        </div>

        <div className="form-section-separator" />

        {/* ======================================================
            3) Financial Information (NON-OPERATIONAL -> Opportunity Notes)
           ====================================================== */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Financial Information</h3>
            <p>These details will be stored as intake notes.</p>
          </div>

          <div className="form-row form-row-2">
            <label htmlFor="firstYearRevenue">
              First Year Revenue Projection
              <input
                id="firstYearRevenue"
                type="text"
                inputMode="numeric"
                value={firstYearRevenue}
                onChange={(e) => setFirstYearRevenue(e.target.value)}
                onBlur={() => onCurrencyBlur(firstYearRevenue, setFirstYearRevenue)}
                placeholder="$0"
              />
            </label>

            <label htmlFor="secondYearRevenue">
              Second Year Revenue Projection
              <input
                id="secondYearRevenue"
                type="text"
                inputMode="numeric"
                value={secondYearRevenue}
                onChange={(e) => setSecondYearRevenue(e.target.value)}
                onBlur={() => onCurrencyBlur(secondYearRevenue, setSecondYearRevenue)}
                placeholder="$0"
              />
            </label>
          </div>

          <div className="form-row">
            <div style={{ fontWeight: 700, marginBottom: 12, color: "#e2e8f0" }}>
              Budget Template (estimated)
            </div>

            <table className="budget-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Amount (USD)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Programmatic Supplies", "programmaticSupplies"],
                  ["Contractor Payments", "contractorPayments"],
                  ["Vendor Payment", "vendorPayments"],
                  ["Scholarships", "scholarships"],
                  ["Travel", "travel"],
                  ["Marketing", "marketing"],
                  ["Administrative Costs", "administrativeCosts"],
                  ["Other", "other"],
                ].map(([label, key]) => (
                  <tr key={key}>
                    <td>{label}</td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={budget[key]}
                        onChange={(e) => setBudget((prev) => ({ ...prev, [key]: e.target.value }))}
                        onBlur={() =>
                          onCurrencyBlur(budget[key], (next) =>
                            setBudget((prev) => ({ ...prev, [key]: next }))
                          )
                        }
                        placeholder="$0"
                        style={{ marginBottom: 0 }}
                      />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ fontWeight: 700 }}>Total</td>
                  <td style={{ fontWeight: 700 }}>{budgetTotalFormatted || "$0"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="form-section-separator" />

        {/* ======================================================
            4) Funding & Grants
           ====================================================== */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Funding & Grants</h3>
            <p>Tell us about your fundraising plans.</p>
          </div>

          <div className="form-row">
            <label>
              Are you fundraising? (Select all that apply)
              <div className="checkbox-group" style={{ marginTop: 12 }}>
                {FUNDRAISING_OPTIONS.map((opt) => (
                  <div className="checkbox-option" key={opt}>
                    <input
                      type="checkbox"
                      checked={fundraisingSelected.includes(opt)}
                      onChange={() => toggleFundraising(opt)}
                      aria-label={opt}
                    />
                    <span>{opt}</span>
                  </div>
                ))}
              </div>
            </label>

            {/* Hidden SF multi-select picklist to submit properly */}
            <select
              ref={fundraisingSelectRef}
              id="00N7z00000m00Ar"
              multiple="multiple"
              name="00N7z00000m00Ar"
              title="Are you fundraising?"
              style={{ display: "none" }}
              readOnly
            >
              {FUNDRAISING_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {hasGovernmentGrants && (
            <div className="form-row">
              <label htmlFor="govFundingExploring">
                Are you pursuing or exploring government funding opportunities?
                <select
                  id="govFundingExploring"
                  value={govFundingExploring}
                  onChange={(e) => setGovFundingExploring(e.target.value)}
                  required
                >
                  <option value="">--Select--</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </label>
            </div>
          )}
        </div>

        <div className="form-section-separator" />

        {/* ======================================================
            5) Organization Details
           ====================================================== */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Organization Details</h3>
            <p>Tell us who is signing / applying.</p>
          </div>

          <div className="form-row">
            <label htmlFor="00N7z00000m0125">
              Applying as Individual or Entity
              <select
                id="00N7z00000m0125"
                name="00N7z00000m0125"
                title="Applying as Individual or Entity"
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                required
              >
                <option value="">--None--</option>
                <option value="Individual">Individual</option>
                <option value="Entity">Entity</option>
              </select>
            </label>
          </div>

          {entityType === "Entity" && (
            <div className="form-row">
              <label htmlFor="00N7z00000m00xF">
                EIN
                <input
                  id="00N7z00000m00xF"
                  name="00N7z00000m00xF"
                  type="text"
                  inputMode="numeric"
                  value={ein}
                  onChange={(e) => setEin(formatEIN(e.target.value))}
                  placeholder="12-3456789"
                  required
                />
              </label>
            </div>
          )}

          {entityType === "Individual" && (
            <div className="form-row">
              <label htmlFor="advisorHomeAddress">
                Advisor Home Address
                <input
                  id="advisorHomeAddress"
                  type="text"
                  value={advisorHomeAddress}
                  onChange={(e) => setAdvisorHomeAddress(e.target.value)}
                  placeholder="Street, City, State, ZIP"
                  required
                />
              </label>
            </div>
          )}
        </div>

        <div className="form-section-separator" />

        {/* ======================================================
            6) Previous Fiscal Sponsorship
           ====================================================== */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Previous Fiscal Sponsorship</h3>
            <p>If applicable, tell us about prior sponsorship and existing funds.</p>
          </div>

          <div className="form-row form-row-2">
            <label htmlFor="fiscallySponsoredBefore">
              Have you been fiscally sponsored before?
              <select
                id="fiscallySponsoredBefore"
                value={fiscallySponsoredBefore}
                onChange={(e) => setFiscallySponsoredBefore(e.target.value)}
              >
                <option value="">--Select--</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label htmlFor="transferFunds">
              Do you have existing funds to transfer?
              <select
                id="transferFunds"
                value={transferFunds}
                onChange={(e) => setTransferFunds(e.target.value)}
              >
                <option value="">--Select--</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
          </div>

          {transferFunds === "Yes" && (
            <div className="form-row">
              <label htmlFor="transferAmount">
                Amount to be transferred (USD)
                <input
                  id="transferAmount"
                  type="text"
                  inputMode="numeric"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  onBlur={() => onCurrencyBlur(transferAmount, setTransferAmount)}
                  placeholder="$0"
                  required
                />
              </label>
            </div>
          )}
        </div>

        <div className="form-section-separator" />

        {/* ======================================================
            7) Social Media (Operational fields, but user inputs only identifier)
           ====================================================== */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Social Media Presence</h3>
            <p>Enter just your identifier/handle (optional).</p>
          </div>

          <div className="form-row">
            <div className="socials-group">
              {/* LinkedIn */}
              <div className="social-input-container">
                <div className="social-icon linkedin" />
                <input
                  type="text"
                  value={linkedInId}
                  onChange={(e) => setLinkedInId(e.target.value)}
                  placeholder='LinkedIn ID'
                />
              </div>

              {/* Instagram */}
              <div className="social-input-container">
                <div className="social-icon instagram" />
                <input
                  type="text"
                  value={instagramId}
                  onChange={(e) => setInstagramId(e.target.value)}
                  placeholder='Instagram handle'
                />
              </div>

              {/* Facebook */}
              <div className="social-input-container">
                <div className="social-icon facebook" />
                <input
                  type="text"
                  value={facebookId}
                  onChange={(e) => setFacebookId(e.target.value)}
                  placeholder='Facebook profile ID'
                />
              </div>
            </div>
          </div>

          {/* Hidden SF fields that receive FULL URLs */}
          <textarea
            ref={linkedInSfRef}
            id="00N7z00000m00Nl"
            name="00N7z00000m00Nl"
            type="text"
            wrap="soft"
            rows={1}
            style={{ display: "none" }}
            readOnly
          />
          <textarea
            ref={instagramSfRef}
            id="00N7z00000m00E6"
            name="00N7z00000m00E6"
            type="text"
            wrap="soft"
            rows={1}
            style={{ display: "none" }}
            readOnly
          />
          <textarea
            ref={facebookSfRef}
            id="00N7z00000m00PN"
            name="00N7z00000m00PN"
            type="text"
            wrap="soft"
            rows={1}
            style={{ display: "none" }}
            readOnly
          />
        </div>

        <div className="form-section-separator" />

        {/* ======================================================
            8) Additional Comments (NON-OP -> Account Notes)
           ====================================================== */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Additional Comments</h3>
            <p>Anything else you want us to know? (optional)</p>
          </div>

          <div className="form-row">
            <label htmlFor="additionalComments">
              Comments (Optional)
              <textarea
                id="additionalComments"
                className="comments-textarea"
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                rows={5}
                placeholder="Is there anything else you'd like us to know about your Cause or organization?"
              />
            </label>
            <div className="character-count">{(additionalComments || "").length}/1000 characters</div>
          </div>
        </div>

        {/* ======================================================
            SF Long Text fields (hidden, submitted to Salesforce)
           ====================================================== */}
        <textarea
          ref={accountNotesRef}
          id="00N7z00000nHLv3"
          name="00N7z00000nHLv3"
          rows="3"
          type="text"
          wrap="soft"
          style={{ display: "none" }}
          readOnly
        />
        <textarea
          ref={opportunityNotesRef}
          id="00N7z00000nHLyH"
          name="00N7z00000nHLyH"
          rows="3"
          type="text"
          wrap="soft"
          style={{ display: "none" }}
          readOnly
        />

        {/* ======================================================
            reCAPTCHA + Submit
           ====================================================== */}
        <div className="form-section">
          <div className="captcha-container">
            <div className="g-recaptcha" data-sitekey="6LdKEpwqAAAAALaRs1U34EXHuG_eDnk5fFXuuPL9" />
          </div>

          <div className="form-section submit-section">
            <div className="submit-container">
              <button type="submit" className="submit-button">
                <span>Submit Application</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ApplicationWebToLead;